import { useEffect, useMemo, useState } from "react";
import { api, dataApi } from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Eye, Plus, Pencil, X, Trash2 } from "lucide-react";
import { BranchSelect, BranchTag, useBranches } from "@/components/BranchBits";
import { toast } from "sonner";

const STATUS_STYLE = {
  planning: "border-primary/40 text-primary",
  active: "border-emerald-500/40 text-emerald-500",
  on_hold: "border-amber-500/40 text-amber-500",
  completed: "border-muted-foreground/40 text-muted-foreground",
  at_risk: "border-red-500/40 text-red-500",
};

// Map the requested UX tab keys to backend project.status values
const TABS = [
  { key: "planning", label: "Pending", statuses: ["planning"] },
  { key: "active", label: "Active", statuses: ["active", "at_risk"] },
  { key: "on_hold", label: "Hold", statuses: ["on_hold"] },
  { key: "completed", label: "Completed", statuses: ["completed"] },
  { key: "cancelled", label: "Cancelled", statuses: ["cancelled"] },
];

function ProjectForm({ open, onOpenChange, projects, onSaved, editing }) {
  const [form, setForm] = useState({ name: "", code: "", client_id: "", description: "", start_date: "", end_date: "", budget: 0, member_ids: [], manager_id: "", branch_id: "" });
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [empSearch, setEmpSearch] = useState("");

  useEffect(() => {
    if (open) {
      api.get("/employees").then((r) => setEmployees(r.data));
      api.get("/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
      if (editing) setForm({
        name: editing.name || "", code: editing.code || "",
        client_id: editing.client_id || "",
        description: editing.description || "", start_date: editing.start_date || "", end_date: editing.end_date || "",
        budget: editing.budget || 0, member_ids: editing.member_ids || [], manager_id: editing.manager_id || "",
        branch_id: editing.branch_id || "",
      });
      else setForm({ name: "", code: "", client_id: "", description: "", start_date: "", end_date: "", budget: 0, member_ids: [], manager_id: "", branch_id: "" });
    }
  }, [open, editing]);

  const toggleMember = (id) => setForm((f) => f.member_ids.includes(id) ? { ...f, member_ids: f.member_ids.filter((x) => x !== id) } : { ...f, member_ids: [...f.member_ids, id] });

  const save = async () => {
    if (!form.name || !form.code || !form.client_id || !form.start_date || !form.end_date) return toast.error("Fill all required fields");
    try {
      if (editing) {
        await api.patch(`/projects/${editing.id}`, form);
        toast.success("Project updated");
      } else {
        await api.post("/projects", form);
        toast.success("Project created");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const filteredEmps = employees.filter((e) => !empSearch || e.full_name.toLowerCase().includes(empSearch.toLowerCase()));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit Project" : "Create Project"}</DialogTitle>
          <DialogDescription>Provide project details and pick team members.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Name *</Label><Input data-testid="proj-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><Label>Code *</Label><Input data-testid="proj-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Client *</Label>
              <Select value={form.client_id} onValueChange={(v) => setForm({ ...form, client_id: v })}>
                <SelectTrigger data-testid="proj-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {clients.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">No clients yet — add one from the Clients page</div>}
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label>Budget</Label><Input type="number" value={form.budget} onChange={(e) => setForm({ ...form, budget: Number(e.target.value) })} /></div>
            <BranchSelect value={form.branch_id} onChange={(v) => setForm({ ...form, branch_id: v })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Start Date *</Label><Input type="date" data-testid="proj-start" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
            <div><Label>End Date *</Label><Input type="date" data-testid="proj-end" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
          </div>
          <div>
            <Label>Manager</Label>
            <Select value={form.manager_id} onValueChange={(v) => setForm({ ...form, manager_id: v })}>
              <SelectTrigger><SelectValue placeholder="Select manager" /></SelectTrigger>
              <SelectContent>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.full_name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <div>
            <Label>Members ({form.member_ids.length} selected)</Label>
            <Input placeholder="Search employees…" value={empSearch} onChange={(e) => setEmpSearch(e.target.value)} className="mb-2" />
            <div className="border rounded-lg p-2 max-h-48 overflow-y-auto scroll-thin">
              {filteredEmps.map((e) => {
                const sel = form.member_ids.includes(e.id);
                return (
                  <button key={e.id} type="button" data-testid={`member-${e.id}`} onClick={() => toggleMember(e.id)}
                    className={`w-full text-left px-2 py-1.5 rounded text-sm flex items-center gap-2 ${sel ? "bg-primary/15" : "hover:bg-accent"}`}>
                    <input type="checkbox" checked={sel} readOnly className="accent-primary" />
                    <span className="flex-1">{e.full_name}</span>
                    <span className="text-[10px] text-muted-foreground">{e.department}</span>
                  </button>
                );
              })}
            </div>
            {form.member_ids.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {form.member_ids.map((id) => {
                  const e = employees.find((x) => x.id === id);
                  return e ? <Badge key={id} variant="secondary" className="gap-1">{e.full_name}<button onClick={() => toggleMember(id)}><X className="w-3 h-3" /></button></Badge> : null;
                })}
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="proj-save" onClick={save}>{editing ? "Save" : "Create"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Projects() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canCreate = isAdmin || user?.role === "branch_admin";
  const { byId: branchById } = useBranches();
  const nav = useNavigate();
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("active");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    dataApi.projects().then((r) => setProjects(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    // Employees are needed to display Project Manager name in the list.
    api.get("/employees").then((r) => setEmployees(r.data)).catch(() => {});
  }, []);

  const removeProject = async (r) => {
    if (!window.confirm(`Delete project "${r.name}"?`)) return;
    try { await api.delete(`/projects/${r.id}`); toast.success("Project deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const empMap = useMemo(() => Object.fromEntries(employees.map((e) => [e.id, e])), [employees]);

  const byTab = useMemo(() => {
    const groups = {};
    for (const t of TABS) {
      groups[t.key] = projects
        .filter((p) => t.statuses.includes(p.status))
        .map((p) => ({ ...p, _manager_name: empMap[p.manager_id]?.full_name || "—" }));
    }
    return groups;
  }, [projects, empMap]);

  const columns = [
    { key: "name", label: "Project", sortable: true, render: (r) => (
      <div><div className="font-medium">{r.name}</div><div className="text-[10px] text-muted-foreground">{r.code}</div></div>
    )},
    { key: "client", label: "Client", sortable: true },
    { key: "_manager_name", label: "Manager", sortable: true, render: (r) => {
      const m = empMap[r.manager_id];
      if (!m) return <span className="text-muted-foreground text-xs">—</span>;
      return (
        <div className="text-sm">
          <div className="font-medium truncate max-w-[180px]" title={m.full_name}>{m.full_name}</div>
          {m.designation && <div className="text-[10px] text-muted-foreground truncate max-w-[180px]">{m.designation}</div>}
        </div>
      );
    }},
    { key: "progress", label: "Progress", sortable: true, render: (r) => (
      <div className="flex items-center gap-2 min-w-[140px]"><Progress value={r.progress} className="h-1.5 flex-1" /><span className="number text-xs">{r.progress}%</span></div>
    )},
    { key: "start_date", label: "Start", sortable: true, className: "number" },
    { key: "end_date", label: "End", sortable: true, className: "number" },
    { key: "member_count", label: "Members", sortable: true, className: "number", render: (r) => (r.member_ids || []).length },
    { key: "branch_id", label: "Branch", sortable: true, render: (r) => <BranchTag id={r.branch_id} byId={branchById} /> },
    { key: "status", label: "Status", sortable: true, render: (r) => <Badge variant="outline" className={`capitalize ${STATUS_STYLE[r.status] || ""}`}>{r.status?.replace("_", " ")}</Badge> },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        testid="projects-header"
        title="Projects"
        subtitle={`${projects.length} projects`}
        actions={canCreate ? <Button data-testid="new-project" onClick={() => { setEditing(null); setDialogOpen(true); }}><Plus className="w-4 h-4 mr-1" />New Project</Button> : null}
      />

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="project-tabs">
          {TABS.map((t) => <TabsTrigger key={t.key} value={t.key} data-testid={`tab-${t.key}`}>{t.label} ({byTab[t.key].length})</TabsTrigger>)}
        </TabsList>
        {TABS.map((t) => (
          <TabsContent key={t.key} value={t.key} className="pt-4">
            <DataTable
              testid={`dt-proj-${t.key}`}
              loading={loading}
              empty={`No ${t.label.toLowerCase()} projects`}
              columns={columns}
              data={byTab[t.key]}
              searchKeys={["name", "code", "client", "_manager_name"]}
              rowActions={(r) => (
                <div className="flex justify-end gap-1">
                  <Button data-testid={`view-proj-${r.id}`} variant="ghost" size="icon" onClick={() => nav(`/projects/${r.id}`)}><Eye className="w-4 h-4" /></Button>
                  {r.can_modify && <Button data-testid={`edit-proj-${r.id}`} variant="ghost" size="icon" onClick={() => { setEditing(r); setDialogOpen(true); }}><Pencil className="w-4 h-4" /></Button>}
                  {r.can_modify && <Button data-testid={`del-proj-${r.id}`} variant="ghost" size="icon" onClick={() => removeProject(r)}><Trash2 className="w-4 h-4 text-red-500" /></Button>}
                </div>
              )}
            />
          </TabsContent>
        ))}
      </Tabs>

      <ProjectForm open={dialogOpen} onOpenChange={setDialogOpen} editing={editing} onSaved={load} />
    </div>
  );
}
